const POSTAL_CODE = /\b\d{4,6}\b/;

const PLACEHOLDER_ADDRESSES = new Set(['location not specified']);

/**
 * Reduce a full formatted address to a short, display-friendly city name for
 * tight UI slots (e.g. the location line on an activity card).
 *
 * Activity addresses come from Google's `formatted_address` (requested with
 * `language=fr`), which renders France addresses as
 * `"<street>, <postcode> <city>, France"`. We isolate the segment carrying the
 * postal code and strip the code, which yields the city regardless of how many
 * leading street segments exist. When no postal code is present we fall back to
 * the segment before the country, then to the whole string.
 *
 * Returns `''` for empty / placeholder addresses so callers can render their own
 * generic label (e.g. "Au lieu") instead of a meaningless value.
 */
export function extractCity(address?: string | null): string {
  if (!address) return '';

  const trimmed = address.trim();
  if (!trimmed || PLACEHOLDER_ADDRESSES.has(trimmed.toLowerCase())) return '';

  const parts = trimmed
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return '';

  const postalIdx = parts.findIndex((part) => POSTAL_CODE.test(part));

  let candidate: string;
  if (postalIdx !== -1) {
    const stripped = parts[postalIdx]
      .replace(POSTAL_CODE, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
    // A "75001 Paris" segment strips down to "Paris"; a lone "75001" segment
    // (postal code on its own) falls through to the next segment.
    candidate = stripped || parts[postalIdx + 1] || parts[postalIdx];
  } else {
    // Last segment is usually the country, so prefer the one before it.
    candidate = parts.length >= 2 ? parts[parts.length - 2] : parts[parts.length - 1];
  }

  return (
    candidate
      .replace(POSTAL_CODE, '')
      .replace(/\s{2,}/g, ' ')
      .trim() || candidate
  );
}
